// React
import { PureComponent, ReactElement, Fragment } from "react";
// Semantic UI
import { Popup } from "semantic-ui-react";
// Constant
import { Images } from "../../../constants";
// Utilities
import { formatPhone } from "../../../utilities/customFunctions";
// Components
import Checkbox from "../checkbox";
// Scss
import styles from "./Input.module.scss";

// Props
type Props = {
  disabled: boolean;
  fixed: boolean;
  required?: boolean;
  coreInputStylesContainer: string;
  coreInputStyles: string;
  label: string;
  input: any;
  type: string;
  placeholder: string;
  labelDetail: string;
  secondaryLink: string;
  secondaryLinkOnClick: () => void;
  meta: { touched: any; error: any; warning: any };
  min: string | number;
  max: string | number;
  extrastyle: string;
  leftIcon: string;
  alternateSource: boolean | string;
  alternateSourceLabel: string;
  secondaryType: string;
  myValue: string;
  tooltip: string | boolean;
  tooltipMessage: string;
  tooltipImage: any;
  dataTestid: string;
  hoverableTooltip?: boolean;
};

// State
type State = {
  useAlternateValue: boolean;
  notEditable: boolean;
  fixed: boolean;
};

/**
 * @export
 * @class ConnectedInput
 * @extends {PureComponent<Props, State>}
 */
export class ConnectedInput extends PureComponent<Props, State> {
  // static
  static propTypes: object = {};

  /**
   * Creates an instance of ConnectedInput.
   * @param {Props} props
   * @memberof ConnectedInput
   */
  constructor(props: Props) {
    super(props);
    this.state = {
      notEditable: props.disabled || false,
      useAlternateValue: false,
      fixed: props.fixed || false,
    };
  }

  /**
   * @memberof ConnectedInput
   */
  onChangeCheckBox = (): void => {
    this.setState((prevState) => {
      return { ...prevState, useAlternateValue: !this.state.useAlternateValue };
    });
  };

  /**
   * @return {*}  {ReactElement}
   * @memberof ConnectedInput
   */
  render(): ReactElement {
    const {
      label,
      input,
      type,
      placeholder,
      labelDetail,
      secondaryLink,
      secondaryLinkOnClick,
      meta: { touched, error, warning },
      min,
      max,
      extrastyle,
      leftIcon,
      alternateSource,
      alternateSourceLabel,
      secondaryType,
      myValue,
      tooltip,
      tooltipMessage,
      tooltipImage,
      dataTestid,
      hoverableTooltip,
    } = this.props;

    const notEditableStyles: string = this.state.notEditable
      ? "notEditableStyles"
      : "";
    const leftIconReferenceStyles: string = leftIcon
      ? "leftIconReferenceStyles"
      : "";

    return (
      <div
        className={`${styles.inputContainer} ${styles[extrastyle]} ${
          styles[this.props.coreInputStylesContainer]
        }`}
      >
        {label && (
          <div className={styles.labelContainer}>
            <div className={styles.label}>
              {label}
              {this.props.required && (
                <span style={{ color: "#d0021b" }}> *</span>
              )}{" "}
              <span className={styles.labelDetail}>{labelDetail}</span>
              {(tooltipMessage || tooltipImage) && (
                <Popup
                  hoverable={hoverableTooltip ?? false}
                  content={
                    <Fragment>
                      {tooltipImage && (
                        <Fragment>
                          <img
                            className={styles.tooltipImage}
                            src={Images[tooltipImage]}
                          />
                        </Fragment>
                      )}
                      {tooltipMessage}
                    </Fragment>
                  }
                  position="top center"
                  trigger={
                    <img src={Images.question} className={styles.tooltip} />
                  }
                />
              )}
            </div>
            <div
              className={styles.secondaryLink}
              onClick={secondaryLinkOnClick}
            >
              {secondaryLink}
            </div>
          </div>
        )}
        <div className={styles.inputWrapper}>
          {leftIcon && (
            <span className={styles.leftIcon}>
              <img height={"20px"} width={"20px"} src={Images[leftIcon]} />
            </span>
          )}
          <input
            autoCapitalize={"false"}
            className={`${styles.inputField} ${styles[notEditableStyles]} ${
              styles[leftIconReferenceStyles]
            } ${styles[this.props.coreInputStyles]}`}
            type={type}
            {...input}
            min={min}
            maxLength={max}
            max={max}
            value={
              this.state.useAlternateValue
                ? alternateSource
                : secondaryType === "phone"
                ? formatPhone(input.value)
                : myValue || input.value
            }
            disabled={this.state.notEditable}
            placeholder={placeholder}
            data-testid={dataTestid}
          />

          {this.state.notEditable && !this.state.fixed && (
            <span
              className={styles.rightText}
              onClick={() => this.setState({ notEditable: false })}
            >
              Edit
            </span>
          )}
        </div>

        {alternateSource && alternateSourceLabel && (
          <Checkbox
            checked={this.state.useAlternateValue}
            label={alternateSourceLabel}
            darkBox={"darkBox"}
            onClick={this.onChangeCheckBox}
          />
        )}

        {touched && error && <span className={styles.error}>{error}</span>}
      </div>
    );
  }
}
